import { useTranslation } from 'react-i18next';

import { prismicClient } from '@/data';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { useBonusEngineClient, userPromosKeys } from '@/providers';
import { KeyTextField } from '@prismicio/types';
import { useQuery } from '@tanstack/react-query';

import { GenericPromo, PromoProgress, Promotion } from '../types';
import { PROMOS_CACHE_TIME } from '../utils/promotions-utils';

/**
 * Gets the promo config from the bonus engine and prismic
 * @param prismicPromo - prismic promo details
 */
export const usePromoConfig = (
    promotion?: Promotion
): {
    promoType: 'segmented' | 'generic';
    optedInTime?: string;
    displayCTA: boolean;
    cta?: string;
    postCTA?: string;
    title?: KeyTextField;
    description?: KeyTextField;
    expirationTime?: string;
    supportingText?: KeyTextField;
    termsAndConditionsUrl?: KeyTextField;
} => {
    const { t } = useTranslation('promohub');
    const promoType = promotion?.data.segmented ? 'segmented' : 'generic';
    // this toggle can be set in both prismic and bonus engine
    const displayCTA =
        (promoType === 'segmented' ? promotion?.bonusEngineDetails?.optInRequired : promotion?.data.display_cta) ??
        false;
    const cta = promotion?.data.cta ?? t('opt_in');
    const optedInTime = promotion?.bonusEngineDetails?.optedInTime;
    const postCTA = promotion?.data.post_cta ?? t('opted_in');
    const title = promotion?.data.title;
    const description = promotion?.data.description;
    const expirationTime = promotion?.bonusEngineDetails?.expirationTime;
    const supportingText = promotion?.data.supporting_text;
    const termsAndConditionsUrl = promotion?.data.terms_and_conditions_url;

    return {
        promoType,
        displayCTA,
        cta,
        postCTA,
        title,
        description,
        optedInTime,
        expirationTime,
        supportingText,
        termsAndConditionsUrl,
    };
};

export const usePromotion = (uid?: string) => {
    return useQuery({
        queryKey: [uid],
        queryFn: async () => {
            if (!uid) {
                return null;
            }
            const promotionDetails = await prismicClient.getByUID('promotion', uid);
            return {
                ...promotionDetails.data,
            };
        },
        enabled: !!uid,
    });
};

export const usePromosStatus = (promoSlugs: string[], enabled: boolean = false) => {
    const bonusEngineAPIClient = useBonusEngineClient();
    const userId = useUserStore(state => state.profile.sub);
    const endpoint = `/submissions/accountId/${userId}?promoSlugs=${promoSlugs.join(',')}`;
    return useQuery<GenericPromo[]>({
        queryKey: userPromosKeys.genericPromotionsStatus(promoSlugs),
        queryFn: async () => bonusEngineAPIClient.fetch(endpoint),
        enabled: enabled && promoSlugs.length > 0,
        staleTime: PROMOS_CACHE_TIME,
    });
};

export const usePromoProgress = (promoId: string) => {
    const bonusEngineAPIClient = useBonusEngineClient();
    const activeProduct = useJurisdictionStore(state => state.product);
    const userId = useUserStore(state => state.profile.sub);

    const endpoint = `/users/${userId}/promotions/${promoId}/progress?productType=${activeProduct.toUpperCase()}`;
    return useQuery<PromoProgress>({
        queryKey: userPromosKeys.promoProgress(promoId, userId),
        queryFn: async () => bonusEngineAPIClient.fetch(endpoint),
        enabled: !!promoId,
    });
};
